import API_ENDPOINTS from "@/common/api/apiEndpoints";
import httpClient from "@/common/api/httpClient";
import httpMethods from "@/common/api/httpMethods";
import EnvConfig from "@/config/env";

export interface SiteSyncStatus {
  syncId?: string;
  status:
    | "not_synced"
    | "idle"
    | "crawling"
    | "synced"
    | "failed"
    | "auth_required";
  isFresh: boolean;
  // True when the row is reported "synced" only because a failed/interrupted
  // crawl still captured usable pages. UI shows a subtle "last crawl was
  // interrupted — re-scan to refresh" hint instead of a hard failure.
  partial?: boolean;
  lastSyncedAt?: string;
  appUrl?: string;
  authDetected?: boolean;
  authStrategy?: string;
  authLoginUrl?: string;
  authUsername?: string;
  hasPassword?: boolean;
  pageCount?: number;
  interactionCount?: number;
  errorMessage?: string;
  generationId?: string;
  crawlProgress?: number;
  crawlPagesFound?: number;
  crawlEstimatedTotal?: number;
  crawlStats?: {
    totalRoutes: number;
    formsFound: number;
    authProtectedRoutes: number;
    publicRoutes: number;
  };
}

export interface SiteSyncStartPayload {
  workspaceVersionId: number;
  appUrl: string;
  /** Force a fresh crawl even if a fresh cache exists (explicit rescan). */
  force?: boolean;
  auth?: {
    strategy: "none" | "form" | "token" | "api_key";
    loginUrl?: string;
    username?: string;
    password?: string;
    useSavedPassword?: boolean;
    token?: string;
    apiKey?: string;
    apiKeyHeader?: string;
  };
}

export interface SiteSyncStartResponse {
  syncId: string;
  status: string;
  generationId?: string;
  discoveredPages?: any[];
  discoveredInteractions?: any[];
  crawlStats?: object;
  lastSyncedAt?: string;
  authDetected?: boolean;
}

export interface SiteSyncPagesResponse {
  syncId: string;
  pages: any[];
  interactions: any[];
  flowEdges?: any[];
  validationResults?: any[];
  crawlStats: object;
}

export interface JourneyStep {
  order: number;
  pagePath: string;
  action: {
    type: string;
    target?: string;
    value?: string;
  };
  expectedResult: string;
  nextPagePath?: string;
}

export interface UserJourney {
  id: string;
  name: string;
  description: string;
  priority: "critical" | "high" | "medium" | "low";
  category: string;
  steps: JourneyStep[];
  preconditions: string[];
  expectedOutcome: string;
  involvedPages: string[];
  involvedForms: string[];
  testDataHints: Record<string, string>;
}

export interface SiteSyncJourneysResponse {
  syncId: string;
  status: "pending" | "complete";
  journeys: UserJourney[];
  siteOverview?: {
    appType: string;
    mainFeatures: string[];
    authRequired: boolean;
    estimatedComplexity: string;
  };
  generatedAt?: string;
  processingTimeMs?: number;
  message?: string;
}

export const getSiteSyncStatus = (versionId: number, url?: string) => {
  let endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.siteSyncStatus}?versionId=${versionId}`;
  if (url) {
    endpoint += `&url=${encodeURIComponent(url)}`;
  }
  // skipPaywallEvent: background read probe — free users silently get null, no modal
  return httpClient<SiteSyncStatus>(httpMethods.GET, endpoint, undefined, {
    skipPaywallEvent: true,
  });
};

export interface SiteSyncListItem {
  syncId: string;
  appUrl: string;
  status: string;
  partial?: boolean;
  authStrategy: string | null;
  lastSyncedAt: string | null;
  pageCount: number;
}

export const getSiteSyncList = (versionId: number) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.siteSyncList}?versionId=${versionId}`;
  return httpClient<{ syncs: SiteSyncListItem[] }>(
    httpMethods.GET,
    endpoint,
    undefined,
    {
      skipPaywallEvent: true,
    },
  );
};

export const startSiteSync = (payload: SiteSyncStartPayload) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.siteSyncStart}`;
  return httpClient<SiteSyncStartResponse>(httpMethods.POST, endpoint, payload);
};

export const updateSiteSyncAuth = (
  syncId: string | number,
  auth: SiteSyncStartPayload["auth"],
) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.siteSyncAuth(syncId)}`;
  return httpClient<{ success: boolean }>(httpMethods.PUT, endpoint, auth);
};

export const resyncSite = (
  syncId: string | number,
  auth?: SiteSyncStartPayload["auth"],
) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.siteSyncResync(syncId)}`;
  return httpClient<SiteSyncStartResponse>(httpMethods.POST, endpoint, {
    auth,
  });
};

export const testSiteAuth = (
  appUrl: string,
  auth?: SiteSyncStartPayload["auth"],
) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.siteSyncTestAuth}`;
  return httpClient<{ success: boolean; message: string }>(
    httpMethods.POST,
    endpoint,
    {
      appUrl,
      auth,
    },
  );
};

export const cancelSiteSync = (syncId: string | number) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.siteSyncCancel(syncId)}`;
  return httpClient<{ success: boolean }>(httpMethods.POST, endpoint, {});
};

export const getSiteSyncPages = (syncId: string | number) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.siteSyncPages(syncId)}`;
  return httpClient<SiteSyncPagesResponse>(httpMethods.GET, endpoint);
};

export const getSiteSyncJourneys = (syncId: string | number) => {
  const endpoint = `${EnvConfig.driverURL}/${API_ENDPOINTS.AI.siteSyncJourneys(syncId)}`;
  return httpClient<SiteSyncJourneysResponse>(httpMethods.GET, endpoint);
};
